import { isValidPluginPermission, type PluginPermission } from '@fias/arche-sdk';
import type { VirtualFileSystem } from '../types';

/**
 * Extract the plugin's declared permissions from its in-progress
 * `fias-plugin.json` manifest in the builder's virtual file system.
 *
 * The builder preview bridge is constructed with these so the preview enforces
 * capability gating identically to a deployed arche. Previously the preview
 * granted a hardcoded superset of permissions, so a plugin that called e.g.
 * `useFiasUser()` without declaring `user:profile:read` worked in preview but
 * hung on an infinite loading state in production. Feeding the *real* declared
 * set makes that failure reproduce in preview, before publish.
 *
 * Returns an empty list when the manifest is missing, unparseable, or declares
 * no valid permissions — which correctly yields a maximally-restricted preview
 * (the honest representation of a plugin that has declared nothing).
 *
 * BEHAVIORAL TWIN of
 * services/api-service/src/routes/arches/plugin-builder/manifest-permissions.ts
 * — keep the two in lockstep. The server twin feeds the preview bridge-token
 * mint; divergent parse semantics would let the client gate allow ops the
 * token can't authorize (or vice versa) for the token's 5-minute lifetime.
 */
export function extractDeclaredPermissions(files: VirtualFileSystem | null): PluginPermission[] {
  const manifest = files?.['fias-plugin.json'];
  if (!manifest?.content) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(manifest.content);
  } catch {
    return [];
  }

  if (!parsed || typeof parsed !== 'object') return [];
  const raw = (parsed as { permissions?: unknown }).permissions;
  if (!Array.isArray(raw)) return [];

  const seen = new Set<PluginPermission>();
  for (const entry of raw) {
    if (typeof entry === 'string' && isValidPluginPermission(entry)) {
      seen.add(entry);
    }
  }
  return [...seen];
}

/** Shape the preview store's mock catalog uses (PluginBridgeOptions.previewStoreProducts). */
export interface DeclaredPreviewProduct {
  productIdentifier: string;
  type: string;
  name: string;
  description: string | null;
  priceCredits: number;
  metadata: Record<string, unknown>;
  subscriptionPeriod?: string;
}

/**
 * Extract the manifest's declared `products` for the preview store's mock
 * catalog, so `useFiasStore()` shows the plugin's real products in the
 * builder preview (purchases stay simulated — no credits move, no
 * entitlement persists). Without this the preview catalog was empty AND the
 * old purchase stub succeeded anyway — a purchase of an undeclared product
 * "worked" with no confirmation UI (kitchen-sink finding, 2026-07-28).
 *
 * Same tolerance posture as extractDeclaredPermissions: missing/unparseable
 * manifest or malformed entries yield an empty/partial catalog, which the
 * store stubs then represent honestly (empty products, PRODUCT_NOT_FOUND).
 */
export function extractDeclaredProducts(files: VirtualFileSystem | null): DeclaredPreviewProduct[] {
  const manifest = files?.['fias-plugin.json'];
  if (!manifest?.content) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(manifest.content);
  } catch {
    return [];
  }

  if (!parsed || typeof parsed !== 'object') return [];
  const raw = (parsed as { products?: unknown }).products;
  if (!Array.isArray(raw)) return [];

  const products: DeclaredPreviewProduct[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const p = entry as Record<string, unknown>;
    if (typeof p.identifier !== 'string' || typeof p.name !== 'string') continue;
    products.push({
      productIdentifier: p.identifier,
      type: typeof p.type === 'string' ? p.type : 'consumable',
      name: p.name,
      description: typeof p.description === 'string' ? p.description : null,
      priceCredits: typeof p.priceCredits === 'number' ? p.priceCredits : 0,
      metadata: {},
      ...(typeof p.subscriptionPeriod === 'string'
        ? { subscriptionPeriod: p.subscriptionPeriod }
        : {}),
    });
  }
  return products;
}
